/**
 * Provider Registry (spec section 8).
 *
 * One place that knows every provider, its type, its capabilities and
 * its current status. The UI reads from here; nothing else imports a
 * concrete provider directly.
 */

import { LocalVideoProvider } from './local-video-provider.js';
import { SVGSceneProvider } from './svg-scene-provider.js';
import { GoogleVidsManualProvider } from './google-vids-manual-provider.js';
import { GoogleVeoProvider } from './google-veo-provider.js';
import { PROVIDER_STATUS } from './base.js';

export { PROVIDER_STATUS };

export const PROVIDER_ORDER = ['local-library', 'procedural-scenes', 'google-vids', 'google-veo'];

export function createProviderRegistry({ getWorkerUrl, assetStore } = {}) {
  const providers = new Map();

  const register = (provider) => {
    providers.set(provider.id, provider);
    return provider;
  };

  register(new LocalVideoProvider({ assetStore }));
  register(new SVGSceneProvider());
  register(new GoogleVidsManualProvider());
  register(new GoogleVeoProvider({ getWorkerUrl }));

  const registry = {
    register,
    get(id) {
      return providers.get(id) ?? null;
    },
    list() {
      return Array.from(providers.values()).sort(
        (a, b) => PROVIDER_ORDER.indexOf(a.id) - PROVIDER_ORDER.indexOf(b.id),
      );
    },
    byType(type) {
      return registry.list().filter((p) => p.type === type);
    },
    /** Providers that can actually produce a clip without a human. */
    automated() {
      return registry.list().filter((p) => p.capabilities.automated && p.type === 'automated');
    },
    manual() {
      return registry.list().filter((p) => p.type === 'manual');
    },
    local() {
      return registry.list().filter((p) => p.type === 'local');
    },
    /** Compact descriptors for the UI. Never includes secrets. */
    async describeAll() {
      const results = await Promise.all(
        registry.list().map(async (provider) => {
          let status;
          try {
            status = await provider.status();
          } catch (error) {
            status = { state: PROVIDER_STATUS.ERROR, detail: error.message };
          }
          return {
            id: provider.id,
            name: provider.name,
            type: provider.type,
            description: provider.description,
            docsUrl: provider.docsUrl,
            capabilities: provider.getCapabilities(),
            status,
          };
        }),
      );
      return results;
    },
  };

  return registry;
}

/** True when the capability set says a human is required somewhere. */
export function requiresManualStep(provider) {
  return Boolean(provider?.capabilities?.manualStepRequired);
}

/** Guard rail: no provider in this project may automate a browser. */
export function assertNoBrowserAutomation(registry) {
  return registry.list().every((p) => p.capabilities.browserAutomation === false);
}
